"use client";

import { useEffect, useRef } from "react";
import type { CdmEvent } from "@/lib/sse/useSSE";
import { play, type Cue } from "./oscillator";

/**
 * Wires the 4-cue oscillator preset to the SSE event stream. Per Decision 8
 * in the plan, all cues sit at a barely-perceptible level — felt more than
 * heard. Audio context is unlocked on the first user gesture (a single
 * keystroke or pointer event is enough; browsers gate AudioContext
 * activation on user interaction).
 *
 *   stateChange      → invoice.FsmTransitioned · document.Attested · policy.Decided (PASS / FAIL / OVERRIDDEN)
 *   moduleUnlock     → ui.ModuleUnlocked
 *   pillarTransition → ui.PillarTransition
 *   indeterminate    → policy.Decided (outcome === INDETERMINATE) ONLY
 *
 * The OVERRIDDEN resolution after an INDETERMINATE deliberately uses the
 * regular stateChange cue — per the plan, the resolve sounds like any
 * other gate decision, the alarm only fires on the ambiguity itself.
 */
export function useAudioCues(events: CdmEvent[]) {
  const lastIdx = useRef(0);
  const ready = useRef(false);

  useEffect(() => {
    const unlock = () => {
      ready.current = true;
      // also poke the audio context so first cue fires synchronously
      try { play("stateChange"); } catch { /* noop */ }
    };
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => {
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("pointerdown", unlock);
    };
  }, []);

  useEffect(() => {
    if (!ready.current) {
      // Catch up the cursor without playing — once unlock fires, only future
      // events trigger cues so we don't dump the entire log into the speakers.
      lastIdx.current = events.length;
      return;
    }
    for (let i = lastIdx.current; i < events.length; i++) {
      const cue = pickCue(events[i]);
      if (cue) play(cue);
    }
    lastIdx.current = events.length;
  }, [events]);
}

function pickCue(ev: CdmEvent): Cue | null {
  switch (ev.eventType) {
    case "invoice.FsmTransitioned":
    case "document.Attested":
      return "stateChange";
    case "ui.ModuleUnlocked":
      return "moduleUnlock";
    case "ui.PillarTransition":
      return "pillarTransition";
    case "policy.Decided": {
      const outcome = (ev.payload?.outcome as string | undefined) ?? "";
      if (outcome === "INDETERMINATE") return "indeterminate";
      // PASS / FAIL / OVERRIDDEN all share the regular state-change cue
      return "stateChange";
    }
    default:
      return null;
  }
}
